import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { AppConfirm } from "../../../utils/PopupUtil";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import TenantAPI from "../../api/TenantAPI";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import { newToastEvent } from "../../../utils/ToastUtil";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";

const ConfirmDelete = async () => {
  const result = await AppConfirm({
    title: "Confirm Record Deletion",
    text: "Are you sure you want to delete this record?",
  });

  return result.isConfirmed;
};

export const handleDeletedRecord = (
  queryClient: QueryClient,
  moduleAPIName: string,
  recordID: string,
  tenantID: string
) => {
  let removalIndex; // TODO: Use this index to readd the record onError.

  queryClient.setQueriesData(
    { queryKey: [tenantID, "records", moduleAPIName] },
    (prev: any) => {
      if (!prev?.records) return;

      let newRecords;
      const first = prev?.records?.[0];
      const isGroupedQuery =
        first?.hasOwnProperty("GroupBy") &&
        first.GroupBy?.records?.length &&
        first?._ModuleAPIName === moduleAPIName;

      // Support for grouped queries included
      if (isGroupedQuery) {
        prev = structuredClone(prev);

        for (const group of prev.records) {
          group.GroupBy.records = group?.GroupBy?.records?.filter(
            (r: any) => r?._id !== recordID
          );
          group.GroupBy.count = group.GroupBy.records.length;
        }

        newRecords = prev.records;
      } else {
        newRecords = prev.records.filter((r: any, i: number) => {
          if (r.id === recordID) {
            removalIndex = i;
            return false;
          }
          return true;
        });
      }

      return { ...prev, records: newRecords };
    }
  );

  return removalIndex;
};

/**
 * useDeleteRecord
 * This hook is used to delete a record from the cloud.
 */
const useDeleteRecord = () => {
  const { cPermissions } = useCloudPermissions();
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);

  const deleteRecord = useMutation({
    mutationFn: async ({
      moduleAPIName,
      recordID,
    }: {
      moduleAPIName: string;
      recordID: string;
      onSuccess?: () => void;
      onError?: () => void;
    }) => {
      if (!cPermissions.cloud_delete_record) {
        return Promise.reject(
          "You do not have permission to delete records in this cloud."
        );
      }

      return TenantAPI.deleteRecord(moduleAPIName, recordID);
    },

    onSuccess: (_, { moduleAPIName, recordID, onSuccess }) => {
      handleDeletedRecord(queryClient, moduleAPIName, recordID, tenantID!);

      callFunctionIfExists(onSuccess);
    },
    onError: () => {
      newToastEvent(
        "warning",
        "User does not have permission to delete records in this cloud."
      );
    },
  });

  return {
    confirmDelete: ConfirmDelete,
    deleteRecord: deleteRecord.mutate,
    recordDeleting: deleteRecord.isPending,
    deleteRecordError: deleteRecord.error
      ? getErrorMessage(deleteRecord.error)
      : null,
  };
};

export default useDeleteRecord;
